/**
 * VehiculePicker — sélecteur de véhicule avec recherche (iter237)
 *
 * Remplace les <Select> déroulants dans toutes les saisies (OR, devis, facture,
 * RDV, gardiennage) : les clients de flotte ayant des dizaines de véhicules,
 * dérouler la liste était impraticable. On tape la plaque, la marque, le modèle
 * ou le VIN et la liste se filtre.
 */
import { useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Car, Check, ChevronsUpDown, Search, X } from "lucide-react";
import VehiculeCard from "@/components/VehiculeCard";

const norm = (s) => String(s || "").toLowerCase().replace(/[\s.-]/g, "");

export const vehiculeLabel = (v) =>
  v ? [v.plaque, [v.marque, v.modele].filter(Boolean).join(" ")].filter(Boolean).join(" · ") : "";

export default function VehiculePicker({
  vehicules = [],
  value,
  onChange,
  allowNone = false,
  noneLabel = "Aucun véhicule",
  placeholder = "Rechercher un véhicule…",
  testId = "vehicule-picker",
  disabled = false,
  className = "",
  // iter255 — affiche la fiche véhicule éditable (plaque, VIN, km…) sous le sélecteur
  showDetails = false,
  onVehiculeUpdated,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  const selected = useMemo(
    () => vehicules.find((v) => v.id === value) || null,
    [vehicules, value],
  );

  const results = useMemo(() => {
    const nq = norm(q);
    if (!nq) return vehicules;
    return vehicules.filter((v) =>
      norm(`${v.plaque}${v.marque}${v.modele}${v.vin || ""}${v.annee || ""}${v.couleur || ""}`).includes(nq),
    );
  }, [vehicules, q]);

  const pick = (id) => {
    onChange?.(id);
    setOpen(false);
    setQ("");
  };

  const picker = (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQ(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={`w-full min-w-0 justify-between font-normal ${selected ? "" : "text-muted-foreground"} ${className}`}
          data-testid={testId}
        >
          <span className="flex items-center gap-2 min-w-0">
            <Car className="w-4 h-4 flex-shrink-0 text-gray-400" />
            <span className="truncate">
              {selected ? vehiculeLabel(selected) : (allowNone ? noneLabel : "Sélectionner un véhicule")}
              {selected?.est_principal ? " ★" : ""}
            </span>
          </span>
          <ChevronsUpDown className="w-4 h-4 flex-shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-[min(92vw,26rem)]"
        onOpenAutoFocus={(e) => { e.preventDefault(); setTimeout(() => inputRef.current?.focus(), 20); }}
        data-testid={`${testId}-popover`}
      >
        <div className="p-2 border-b border-gray-100 relative">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <Input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={placeholder}
            className="pl-8 h-9"
            data-testid={`${testId}-search`}
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1" data-testid={`${testId}-list`}>
          {allowNone && (
            <button
              type="button"
              onClick={() => pick("")}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-600"
              data-testid={`${testId}-option-none`}
            >
              <X className="w-3.5 h-3.5 text-gray-400" /> {noneLabel}
            </button>
          )}
          {results.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-gray-500" data-testid={`${testId}-empty`}>
              Aucun véhicule ne correspond à « {q} »
            </div>
          ) : (
            results.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => pick(v.id)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between gap-2 ${v.id === value ? "bg-emerald-50" : ""}`}
                data-testid={`${testId}-option-${v.id}`}
              >
                <span className="min-w-0">
                  <span className="font-medium text-gray-900">
                    {v.plaque || "Sans plaque"}{v.est_principal ? " ★" : ""}
                  </span>
                  <span className="block text-xs text-gray-500 truncate">
                    {[v.marque, v.modele, v.annee].filter(Boolean).join(" ") || "—"}
                  </span>
                </span>
                {v.id === value && <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
              </button>
            ))
          )}
        </div>
        {vehicules.length > 8 && (
          <div className="px-3 py-1.5 border-t border-gray-100 text-[11px] text-gray-500">
            {results.length} / {vehicules.length} véhicules
          </div>
        )}
      </PopoverContent>
    </Popover>
  );

  if (!showDetails || !selected) return picker;

  return (
    <div className="space-y-2 w-full min-w-0" data-testid={`${testId}-with-details`}>
      {picker}
      {/* iter255 — même fiche véhicule éditable que dans l'OR (plaque, VIN, km, détails) */}
      <VehiculeCard
        key={selected.id}
        vehicule={selected}
        onUpdate={(data) => onVehiculeUpdated?.(data)}
      />
    </div>
  );
}
